import type { ConsumerBillType } from "@/types"
import { ColumnDef } from "@tanstack/react-table"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Icons } from "@/components/icons"

import { DataTableColumnHeader } from "../../data-table"
import { SubmitPayment } from "../../forms/submit-payment"
import { ConsumerBillOperations } from "../operations/consumer-bill-operations"

export const consumerBillColumns = (
  status: string,
): ColumnDef<ConsumerBillType>[] => [
  {
    id: "select",
    header: ({ table }) => {
      // ✅ Hide header checkbox if status === "all"
      if (status === "all") return null

      return (
        <Checkbox
          className="border-muted-foreground bg-muted"
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      )
    },
    cell: ({ row }) => {
      // ✅ Hide row checkbox if status === "all"
      if (status === "all") return null

      return (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      )
    },
  },
  {
    accessorKey: "billing_id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Billing ID" />
    ),
    cell: ({ row }) => {
      const value = row.original.billing_id
      return <div>{value}</div>
    },
  },
  {
    accessorKey: "consumer",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Consumer" />
    ),
    cell: ({ row }) => {
      const value = row.original.consumer
      return <div>{value}</div>
    },
  },
  {
    accessorKey: "zone",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Zone" />
    ),
    cell: ({ row }) => {
      const value = row.original.zone
      return <div>{value}</div>
    },
  },
  {
    accessorKey: "book",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Book" />
    ),
    cell: ({ row }) => {
      const value = row.original.book
      return <div>{value}</div>
    },
  },
  {
    accessorKey: "meter_num",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Meter No" />
    ),
    cell: ({ row }) => {
      const value = row.original.meter_num
      return <div>{value}</div>
    },
  },
  {
    accessorKey: "acc_id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Acc No" />
    ),
    cell: ({ row }) => {
      const value = row.original.acc_id
      return <div>{value}</div>
    },
  },
  {
    accessorKey: "units",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Units" />
    ),
    cell: ({ row }) => {
      const value = row.original.units
      return <div>{value}</div>
    },
  },
  {
    accessorKey: "date",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Date" />
    ),
    cell: ({ row }) => {
      const date = new Date(row.original.date as string)
      const formattedDate = Intl.DateTimeFormat("en-US", {
        weekday: "short",
        month: "long",
        day: "numeric",
        year: "numeric",
      }).format(date)
      return <div>{formattedDate}</div>
    },
  },
  {
    accessorKey: "due_date",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Due Date" />
    ),
    cell: ({ row }) => {
      const date = new Date(row.original.due_date as string)
      const formattedDate = Intl.DateTimeFormat("en-US", {
        weekday: "short",
        month: "long",
        day: "numeric",
        year: "numeric",
      }).format(date)
      return <div>{formattedDate}</div>
    },
  },
  {
    accessorKey: "due_amount",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Due Amount" />
    ),
    cell: ({ row }) => {
      const value = row.original.due_amount
      return <div>₱{value}</div>
    },
  },
  {
    accessorKey: "total_amount",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Total Amount" />
    ),
    cell: ({ row }) => {
      const value = row.original.total_amount
      return <div>₱{value}</div>
    },
  },
  {
    accessorKey: "actions",
    header: () => {
      return <div className="text-center dark:text-white">Actions</div>
    },
    cell: ({ row }) => {
      const isPaid = row.original.payment_status.toLowerCase() === "paid"
      return (
        <div className="flex justify-center gap-2 px-4 py-2 text-right md:py-0 md:text-left">
          {isPaid ? (
            <Button variant="outline" size="sm" disabled>
              <Icons.check className="mr-2" /> Paid
            </Button>
          ) : (
            <>
              <SubmitPayment row={row.original} />
              <ConsumerBillOperations data={row.original} />
            </>
          )}
        </div>
      )
    },
  },
]
